import React from 'react';
import {
  Settings, Zap, Target, Activity,
  AlertTriangle, Gauge, TrendingUp,
} from 'lucide-react';
import ServicePage from '../../components/enterprise/ServicePage';

const SolarAdvancedAnalyticsPage = () => (
  <ServicePage
    hero={{
      eyebrow: 'Solar Advanced Analytics',
      title: (
        <>
          Drive superior performance <em>from solar assets</em>
        </>
      ),
      lede: 'Assured performance improvement of operational renewable energy assets.',
      image: '/assets/img/general/solar-6816101_1280.jpg',
      imageAlt: 'Large-scale solar installation',
      actions: [
        { label: 'Book a demo', to: '/contact' },
        { label: 'Explore all services', to: '/service' },
      ],
      metrics: [
        { value: '30%', label: 'Efficiency increase' },
        { value: '40%', label: 'Cost reduction' },
        { value: '99.8%', label: 'Uptime' },
        { value: '<5min', label: 'Response time' },
      ],
    }}
    overview={{
      eyebrow: 'Overview',
      title: 'Raw plant data, turned into sustained performance',
      lede: 'Leverage advanced analytics to transform raw plant data into actionable insights, benchmark site performance, detect anomalies, and optimize output — delivering maximum ROI from every panel and string.',
      checklist: [
        'Increase solar efficiency by 20-30%',
        'Reduce maintenance costs by 40%',
        'Minimize downtime through automation',
        'Optimize energy production 24/7',
      ],
      image: '/assets/img/general/photovoltaic-2814504_1280.jpg',
      imageAlt: 'Photovoltaic strings under analysis',
      imageCaption: 'Benchmark every site, string and inverter against its own expected output',
    }}
    capabilities={{
      eyebrow: 'Capabilities',
      title: 'Capabilities that matter',
      lede: 'Turn solar data into sustained performance gains.',
      items: [
        {
          icon: Settings,
          title: 'Automated workflows',
          description: 'Intelligent automation that identifies root causes and recommends corrective actions.',
        },
        {
          icon: Zap,
          title: 'Real-time optimization',
          description: 'Near real-time analysis and optimization to maximize solar panel efficiency.',
        },
        {
          icon: Target,
          title: 'Predictive maintenance',
          description: 'Proactive identification of potential issues before they impact performance.',
        },
        {
          icon: Activity,
          title: 'Performance monitoring',
          description: 'Continuous monitoring of solar panel performance and energy output.',
        },
      ],
    }}
    process={{
      eyebrow: 'Workflow',
      title: 'From raw signal to corrective action',
      lede: 'Four stages run continuously against every site in the portfolio.',
      steps: [
        {
          title: 'Data collection',
          description: 'Continuous monitoring of solar panel performance, weather conditions, and energy output.',
        },
        {
          title: 'AI analysis',
          description: 'Advanced algorithms analyze data to identify patterns and potential issues.',
        },
        {
          title: 'Root cause identification',
          description: 'Automated detection of performance issues and their underlying causes.',
        },
        {
          title: 'Corrective actions',
          description: 'Intelligent recommendations for immediate optimization and maintenance.',
        },
      ],
    }}
    coverage={{
      eyebrow: 'Analytics use cases',
      title: 'Where the analytics are applied',
      items: [
        {
          icon: Gauge,
          title: 'Performance benchmarking',
          description: 'Compare assets or plants to set baselines and identify leaders and laggards.',
        },
        {
          icon: AlertTriangle,
          title: 'Anomaly detection',
          description: 'Spot underperforming strings, inverters, or environmental deviations rapidly.',
        },
        {
          icon: TrendingUp,
          title: 'Output optimization',
          description: 'Data-driven recommendations to boost and sustain generation over lifetime.',
        },
      ],
    }}
    cta={{
      title: 'Ready to elevate solar performance?',
      body: 'Schedule a session to see analytics tailored to your portfolio.',
      primary: { label: 'Book a demo', to: '/contact' },
      secondary: { label: 'View all services', to: '/service' },
    }}
  />
);

export default SolarAdvancedAnalyticsPage;
